import { Suspense } from "react";
import PropTypes from "prop-types";
import AppLayout from "./AppLayout";
import AppSidebar from "./AppSidebar";
import AppHeader from "./AppHeader";
import useAppModernisation from "../../hooks/useAppModernisation";

/**
 * Common layout wrapper for modern web screens (Phase 2).
 * Provides consistent AppSidebar and AppHeader across all modern web routes.
 *
 * Usage:
 * <ModernWebLayout title="Page Title" pageSidebar={<PageSidebar />}>
 *   <MainContent>
 *     // Your page content
 *   </MainContent>
 * </ModernWebLayout>
 */
const ModernWebLayout = ({
  showAppSidebar = true,
  showSidebar = true,
  pageSidebar = null,
  stickyPageSidebar = null,
  stickyPageHeader = null,
  children,
}) => {
  const { isWebModernisationEnabled, isCalculatorsLegacyIntegration } = useAppModernisation();

  // For Drop 2 (legacy integration), hide AppSidebar and AppHeader
  const shouldShowAppSidebar = showAppSidebar && !isCalculatorsLegacyIntegration;
  const shouldShowAppHeader = !isCalculatorsLegacyIntegration;
  const shouldShowMobileHeader = !isCalculatorsLegacyIntegration;

  return (
    <div className={isWebModernisationEnabled ? "modernisation-enabled" : ""}>
      <Suspense fallback={<div className="loader"></div>}>
        <AppLayout
          showSidebar={showSidebar} // Control pageSidebar
          showMainSidebar={shouldShowAppSidebar} // Control AppSidebar based on flag
          pageSidebar={pageSidebar}
          stickyPageSidebar={stickyPageSidebar}
          stickyPageHeader={stickyPageHeader}
          useMainContentWrapper={false}
          appSidebar={shouldShowAppSidebar ? <AppSidebar /> : null}
          appHeader={shouldShowAppHeader ? <AppHeader /> : null}
          showMobileHeader={shouldShowMobileHeader}
        >
          {children}
        </AppLayout>
      </Suspense>
    </div>
  );
};

ModernWebLayout.propTypes = {
  title: PropTypes.string,
  showAppSidebar: PropTypes.bool,
  pageSidebar: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export default ModernWebLayout;
